import { BodilColor } from '~src/branding';
import { Heading, Text } from '~src/components/Typography';
import { useCustomerContext } from '~src/context/CustomerContext';
import { useLeadOrigin } from '~src/hooks/useLeadOrigin';
import { CustomerDetails as CustomerDetailsBase, ResidenceInput } from '~src/model';

import { Button } from '../Button';
import { CustomerInformation } from '../form/Information/Customer';
import { OfferTypeSelector } from '../EstimatePage';
import {
  ADDITIONAL_INFORMATION_BULLETPOINTS,
  ADDITIONAL_INFORMATION_DISCLAIMER,
  ADDITIONAL_INFORMATION_INCENTIVE,
  ADDITIONAL_INFORMATION_QUESTIONS,
  ADDITIONAL_INFORMATION_SUBTITLE,
  ADDITIONAL_INFORMATION_TITLE,
  CREATING_OFFER_MESSAGE,
} from './constants';
import { useState } from 'react';
import { Select } from '../Select';
import { BulletPoints } from '../BulletPoints';
import { useDisplayContext } from '~src/context/DisplayContext';
import { TextWithHighlight } from '../TextWithHighlights';

type CustomerDetails = CustomerDetailsBase;

type Props = {
  isLoading?: boolean;
  onConfirm: (parameters: Partial<CustomerDetails>) => void;
  residence?: Partial<Omit<ResidenceInput, 'address'>>;
};

export const SendManualOffer = ({ isLoading, onConfirm, residence }: Props) => {
  const [page, setPage] = useState(1);

  return (
    <>
      <FirstPage residence={residence} isLoading={isLoading} page={page} setPage={setPage} />

      <SecondPage residence={residence} isLoading={isLoading} page={page} onConfirm={onConfirm} />

      {isLoading ? <Text>{CREATING_OFFER_MESSAGE}</Text> : null}
    </>
  );
};

type FirstPageProps = Omit<Props, 'onConfirm'> & { page: number; setPage: (page: number) => void };

const FirstPage = ({ isLoading, residence, page, setPage }: FirstPageProps) => {
  const { customer, setCustomer } = useCustomerContext();
  const { origin } = useLeadOrigin();

  const { firstName, lastName, email, phoneNumber, jyskeBankDataConsent } = customer ?? {};

  const fadeInStyle = page === 1 ? { opacity: 1, display: 'inherit' } : {};
  const fadeOutStyle = page !== 1 ? { opacity: 0, display: 'none' } : {};

  const fadeStyle = { transition: 'opacity 0.5s', opacity: 0, ...fadeInStyle, ...fadeOutStyle };

  return (
    <div style={fadeStyle}>
      <CustomerInformation {...{ customer, setCustomer, isLoading }} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifySelf: 'center',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        <OfferTypeSelector
          style={{ height: '48px' }}
          buttonStyle={{ color: BodilColor.base.default }}
          backgroundStyle={{ background: BodilColor.greyscale['10'], opacity: 1 }}
          fuelType={residence?.primaryHeating?.fuelType}
          type='checkmark'
        />

        <Button
          type='callToAction'
          isLoading={isLoading}
          disabled={
            !firstName?.length ||
            !lastName?.length ||
            !email?.length ||
            !phoneNumber?.length ||
            (phoneNumber.length > 0 && phoneNumber.length < 8) ||
            (origin === 'jyskebank' && !jyskeBankDataConsent)
          }
          onClick={() => (customer ? setPage(2) : undefined)}
        >
          Fortsæt
        </Button>
      </div>
    </div>
  );
};

type SecondPageProps = Omit<Props, 'onConfirm'> & {
  page: number;
  onConfirm: (parameters: Partial<CustomerDetails>) => void;
};

const SecondPage = ({ isLoading, page, onConfirm }: SecondPageProps) => {
  const { isMobile } = useDisplayContext();
  const { customer, setCustomer } = useCustomerContext();

  const fadeInStyle = page === 2 ? { opacity: 1 } : {};
  const fadeOutStyle = page !== 2 ? { opacity: 0, display: 'none' } : {};

  const fadeStyle = { transition: 'opacity 0.5s', opacity: 0, ...fadeInStyle, ...fadeOutStyle };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifySelf: 'center',
        textAlign: 'center',
        gap: '32px',
        ...fadeStyle,
      }}
    >
      <Heading>
        {TextWithHighlight({ text: ADDITIONAL_INFORMATION_TITLE, highlights: { '2.000 kr.': 'orange' } })}
      </Heading>

      <Heading level={2}>
        {TextWithHighlight({ text: ADDITIONAL_INFORMATION_SUBTITLE, highlights: { '2.000 kr.': 'orange' } })}
      </Heading>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div style={{ justifyItems: 'start', textAlign: 'left' }}>
          <div>
            <BulletPoints entries={ADDITIONAL_INFORMATION_BULLETPOINTS}></BulletPoints>

            <Heading level={4}>{ADDITIONAL_INFORMATION_DISCLAIMER}</Heading>
          </div>

          <div style={{ marginBottom: 0, marginTop: '30px' }}>
            <Heading level={3}>{ADDITIONAL_INFORMATION_INCENTIVE}</Heading>
          </div>
        </div>

        <div>
          {ADDITIONAL_INFORMATION_QUESTIONS.map(({ label, value, options }) => {
            return (
              <Select
                style={{ width: '100%', marginBottom: '8px' }}
                label={label}
                options={options}
                onSelect={option =>
                  setCustomer?.({ ...customer, scoringFields: { ...customer?.scoringFields, [value]: option } })
                }
              />
            );
          })}
        </div>
      </div>
      <Button type='callToAction' isLoading={isLoading} onClick={() => (customer ? onConfirm(customer) : undefined)}>
        Fortsæt
      </Button>
    </div>
  );
};
